import { SegmentedControl } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { List } from 'src/design/atoms/List';
import { Loader } from 'src/design/atoms/Loader';
import { Popover } from 'src/design/molecules/Popover';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Input } from 'src/design/atoms/Input';
import { MultiSelect } from 'src/design/atoms/MultiSelect';
import { Switch } from 'src/design/atoms/Switch';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { IconInfoCircle } from '@tabler/icons-react';
import { useFormik } from 'formik';
import React, { useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as Yup from 'yup';
import { useKnowledgeArticle } from 'src/api/queries/knowledgeArticles/useKnowledgeArticle';
import {
  KnowledgeArticleUpsertRequestDto,
  useArchiveKnowledgeArticle,
  useCreateKnowledgeArticle,
  useRestoreKnowledgeArticle,
  useUpdateKnowledgeArticle,
} from 'src/api/queries/knowledgeArticles/useKnowledgeArticleMutations';
import { useOffices } from 'src/api/queries/organization/useOffices';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { DangerButton } from 'src/design/atoms/DangerButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Modal } from 'src/design/molecules/Modal';
import {
  applyValidationErrors,
  getErrorMessage,
  formatFixed,
  logger,
  showErrorToast,
} from 'src/utils';

export interface IKnowledgeEditorProps {
  articleId: number | null | undefined;
  opened: boolean;
  onClose: () => void;
}

const MAX_TITLE_LENGTH = 200;
const MAX_SUMMARY_LENGTH = 300;
const MAX_BODY_BYTES = 20_480;

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Title is required')
    .max(
      MAX_TITLE_LENGTH,
      `Title must be ${MAX_TITLE_LENGTH} characters or fewer`,
    ),
  summary: Yup.string()
    .required('Summary is required')
    .max(
      MAX_SUMMARY_LENGTH,
      `Summary must be ${MAX_SUMMARY_LENGTH} characters or fewer`,
    ),
  body: Yup.string()
    .required('Body is required')
    .test(
      'body-byte-length',
      `Body must be ${MAX_BODY_BYTES} bytes (UTF-8 encoded) or fewer`,
      (value) => bodyByteLength(value ?? '') <= MAX_BODY_BYTES,
    ),
  officeIds: Yup.array().of(Yup.number()),
  isPublished: Yup.boolean().required(),
});

const defaultValues: KnowledgeArticleUpsertRequestDto = {
  title: '',
  summary: '',
  body: '',
  isPublished: false,
  officeIds: [],
};

/** UTF-8 byte length, matching the backend's `Encoding.UTF8.GetByteCount` cap — not `.length` (chars), which undercounts multibyte content. */
const bodyByteLength = (body: string) => new TextEncoder().encode(body).length;

const BODY_PLACEHOLDER = `## Overtime policy

- Weekday overtime requires supervisor approval
- **Weekend work** is billed at 1.5x`;

export const KnowledgeEditor: React.FC<IKnowledgeEditorProps> = ({
  articleId,
  opened,
  onClose,
}) => {
  const isEditing = articleId != null;
  const [view, setView] = useState<'write' | 'preview'>('write');
  const [tipsOpened, setTipsOpened] = useState(false);

  const { data: article, isLoading: isLoadingArticle } = useKnowledgeArticle(
    articleId ?? undefined,
  );
  const { data: offices } = useOffices();
  const officeOptions = useMemo(
    () =>
      (offices ?? []).map((office) => ({
        value: String(office.id),
        label: office.name,
      })),
    [offices],
  );

  const createArticle = useCreateKnowledgeArticle();
  const updateArticle = useUpdateKnowledgeArticle();
  const archiveArticle = useArchiveKnowledgeArticle();
  const restoreArticle = useRestoreKnowledgeArticle();

  const formik = useFormik<KnowledgeArticleUpsertRequestDto>({
    enableReinitialize: true,
    initialValues: article
      ? {
          title: article.title,
          summary: article.summary,
          body: article.body,
          isPublished: article.isPublished,
          officeIds: article.offices.map((o) => o.id),
        }
      : defaultValues,
    validationSchema,
    onSubmit: async (values, { setFieldError }) => {
      try {
        if (isEditing && articleId != null) {
          await updateArticle.mutateAsync({ id: articleId, request: values });
        } else {
          await createArticle.mutateAsync(values);
        }
        onClose();
      } catch (err) {
        logger.error('Failed to save knowledge article', { err });
        const { applied, unmatchedMessages } = applyValidationErrors(
          err,
          Object.keys(values),
          setFieldError,
        );
        if (applied && unmatchedMessages.length === 0) {
          showErrorToast('Please fix the highlighted fields below.');
        } else {
          showErrorToast(getErrorMessage(err));
        }
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setView('write');
    setTipsOpened(false);
    onClose();
  };

  const bodyBytes = bodyByteLength(formik.values.body);
  const isArchived = !!article?.archivedAt;
  const isSaving = createArticle.isPending || updateArticle.isPending;

  const actions: React.ReactNode[] = [];
  if (isEditing && !isArchived) {
    actions.push(
      <DangerButton
        key="archive"
        size="sm"
        loading={archiveArticle.isPending}
        onClick={() => articleId != null && archiveArticle.mutate(articleId)}
      >
        Archive
      </DangerButton>,
    );
  }
  if (isEditing && isArchived) {
    actions.push(
      <SecondaryButton
        key="restore"
        size="sm"
        loading={restoreArticle.isPending}
        onClick={() => articleId != null && restoreArticle.mutate(articleId)}
      >
        Restore
      </SecondaryButton>,
    );
  }
  actions.push(
    <SaveButton
      key="save"
      size="sm"
      loading={isSaving}
      disabled={!formik.isValid || (isEditing && !formik.dirty)}
      onClick={() => formik.handleSubmit()}
    >
      Save
    </SaveButton>,
  );

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditing ? 'Edit Knowledge Article' : 'New Knowledge Article'}
      size="xl"
      actions={actions}
    >
      {isEditing && isLoadingArticle ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="md">
          <Group justify="flex-end">
            <Popover
              opened={tipsOpened}
              onChange={setTipsOpened}
              width={360}
              position="bottom-end"
              withArrow
              shadow="md"
              withinPortal
              // Duration 0 keeps the dropdown's DOM insertion synchronous
              // with the click that opens it, instead of deferring behind
              // a requestAnimationFrame fade-in.
              transitionProps={{ duration: 0 }}
            >
              <Popover.Target>
                <BasicButton
                  variant="subtle"
                  size="compact-xs"
                  color="gray"
                  leftSection={<IconInfoCircle size={14} />}
                  onClick={() => setTipsOpened((o) => !o)}
                >
                  Writing tips
                </BasicButton>
              </Popover.Target>
              <Popover.Dropdown>
                <List size="sm">
                  <List.Item>
                    Cover one topic per article — split unrelated policies into
                    separate articles.
                  </List.Item>
                  <List.Item>
                    The title and summary are how the assistant finds an article
                    — write them like the question someone would ask.
                  </List.Item>
                  <List.Item>
                    Keep the body plain and skimmable: short paragraphs,
                    headings, and bullet lists.
                  </List.Item>
                  <List.Item>
                    Assign offices only when the information differs by office;
                    leave blank to apply company-wide.
                  </List.Item>
                  <List.Item>
                    Publish when ready — draft articles are invisible to the
                    assistant.
                  </List.Item>
                </List>
              </Popover.Dropdown>
            </Popover>
          </Group>

          {isArchived && (
            <Alert color="yellow" title="Archived">
              This article is archived and is not visible to the assistant.
              Restore it to make it available again.
            </Alert>
          )}

          <TextInput
            label="Title"
            withAsterisk
            name="title"
            value={formik.values.title}
            onChange={(e) => formik.setFieldValue('title', e.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.title && formik.errors.title}
          />

          <Textarea
            label="Summary"
            withAsterisk
            name="summary"
            description={`The assistant uses the title and summary to decide when to read this article — make them specific. ${formik.values.summary.length}/${MAX_SUMMARY_LENGTH} characters`}
            autosize
            minRows={2}
            value={formik.values.summary}
            onChange={(e) => formik.setFieldValue('summary', e.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.summary && formik.errors.summary}
          />

          <MultiSelect
            label="Offices"
            description="Leave blank to make this article available globally"
            placeholder="Global (no offices selected)"
            data={officeOptions}
            value={formik.values.officeIds.map(String)}
            onChange={(values) =>
              formik.setFieldValue('officeIds', values.map(Number))
            }
            searchable
            clearable
          />

          <Input.Wrapper
            label="Body"
            withAsterisk
            description="Supports Markdown formatting — headings, lists, bold, tables, links. Use Preview to see how it will look."
            error={formik.touched.body && formik.errors.body}
          >
            <Group justify="flex-end" mb={4}>
              <SegmentedControl
                size="xs"
                value={view}
                onChange={(v) => setView(v as 'write' | 'preview')}
                data={[
                  { label: 'Write', value: 'write' },
                  { label: 'Preview', value: 'preview' },
                ]}
              />
            </Group>
            {view === 'write' ? (
              <Textarea
                aria-label="Body"
                name="body"
                placeholder={BODY_PLACEHOLDER}
                autosize
                minRows={10}
                value={formik.values.body}
                onChange={(e) => formik.setFieldValue('body', e.target.value)}
                onBlur={formik.handleBlur}
              />
            ) : (
              <Box
                p="sm"
                style={{
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: 4,
                  minHeight: 200,
                }}
              >
                <Markdown remarkPlugins={[remarkGfm]}>
                  {formik.values.body}
                </Markdown>
              </Box>
            )}
            <Text
              size="xs"
              c={bodyBytes > MAX_BODY_BYTES ? 'red' : 'dimmed'}
              mt={4}
            >
              {formatFixed(bodyBytes / 1024, { decimals: 1 })} KB of{' '}
              {formatFixed(MAX_BODY_BYTES / 1024, { decimals: 0 })} KB
            </Text>
          </Input.Wrapper>

          <Group justify="space-between">
            <Switch
              label="Published"
              description="Draft articles are never surfaced to the assistant"
              checked={formik.values.isPublished}
              onChange={(e) =>
                formik.setFieldValue('isPublished', e.currentTarget.checked)
              }
            />
            {formik.values.officeIds.length === 0 && (
              <Badge color="blue" variant="light">
                Global
              </Badge>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
